import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { keysV2, useWaiverTemplates } from "../../hooks/v2";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { useQueryClient } from "@tanstack/react-query";
import { Modal } from "../ui/Modal";
import { SignaturePad } from "./SignaturePad";

interface Props {
  open: boolean;
  guardianId: string;
  guardianName?: string;
  kids: Array<{ id: string; name: string }>;
  onClose: () => void;
  onSigned: (waiverId: string, expiresAt: string | null) => void;
}

/**
 * The parent reads the CURRENT template text, types their name and signs. Only the template id travels to the server:
 * the legal text that is hashed and stored is the server's copy, so what was shown is exactly what is evidenced.
 */
export function WaiverDialog({ open, guardianId, guardianName, kids, onClose, onSigned }: Props) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const qc = useQueryClient();
  const templates = useWaiverTemplates(locale, open);
  const tpl = templates.data?.[0];
  const [signer, setSigner] = useState(guardianName ?? "");
  const [svg, setSvg] = useState<string | null>(null);
  const [media, setMedia] = useState(false);
  const [chosen, setChosen] = useState<string[]>(kids.map((k) => k.id));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setSigner(guardianName ?? ""); setSvg(null); setMedia(false); setChosen(kids.map((k) => k.id)); }
  }, [open, guardianName, kids]);

  const ready = !!tpl && signer.trim().length >= 2 && !!svg && chosen.length > 0;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!ready || !tpl || !svg) return;
    setBusy(true);
    try {
      const r = await api.post<{ id: string; expiresAt: string | null }>("/waivers", { guardianId, templateId: tpl.id, kidIds: chosen, signerName: signer.trim(), signatureSvg: svg, acceptedMedia: media });
      for (const k of chosen) void qc.invalidateQueries({ queryKey: ["v2", "waiver-status", k] });
      void qc.invalidateQueries({ queryKey: keysV2.templates(locale) });
      toast(t("waiver.signed"), "success");
      onSigned(r.id, r.expiresAt);
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} title={t("waiver.title")} onClose={onClose} size="lg">
      {templates.isSuccess && !tpl ? (
        <p role="alert" className="rounded-xl bg-amber-50 p-3 text-xs font-bold text-amber-800">{t("waiver.noTemplate")}</p>
      ) : (
        <form onSubmit={submit} className="space-y-4" aria-label={t("waiver.title")}>
          {tpl && (
            <div>
              <h3 className="text-sm font-black">{tpl.title}</h3>
              <div className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-2xl bg-gray-50 p-3 text-xs leading-relaxed text-gray-700" data-testid="waiver-text">{tpl.body_markdown}</div>
            </div>
          )}
          <fieldset>
            <legend className="label">{t("waiver.kids")}</legend>
            <div className="flex flex-wrap gap-2">
              {kids.map((k) => (
                <label key={k.id} className="flex items-center gap-1.5 rounded-xl border border-cosmic-silver px-3 py-1.5 text-xs font-bold">
                  <input type="checkbox" checked={chosen.includes(k.id)} onChange={(e) => setChosen(e.target.checked ? [...chosen, k.id] : chosen.filter((x) => x !== k.id))} />
                  {k.name}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label className="label" htmlFor="wv-name">{t("waiver.signer")}</label>
            <input id="wv-name" className="field" value={signer} onChange={(e) => setSigner(e.target.value)} />
          </div>
          <SignaturePad onChange={setSvg} />
          <label className="flex items-center gap-2 text-xs font-bold text-gray-700">
            <input type="checkbox" checked={media} onChange={(e) => setMedia(e.target.checked)} />
            {t("waiver.acceptMedia")}
          </label>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" disabled={!ready || busy}>{busy ? t("common.loading") : t("waiver.submit")}</button>
          </div>
        </form>
      )}
    </Modal>
  );
}
